import apiClient from '@/shared/lib/apiClient';
import type { Collect, AgreementInfo, AgreementDetail, SubmitPunchcardPayload } from '../types';
import { PUNCHCARD_TYPE, COLLECTOR_UUID, COLLECTOR_TYPE, COLLECTOR_PLATFORM } from '../constants';

export const punchcardService = {
  getCollects: (agreementUuid: string): Promise<Collect[]> =>
    apiClient
      .get<Collect[]>(`/agreements/${agreementUuid}/collects/punchcard`)
      .then((res) => res.data),

  /**
   * Submits a punchcard collect. `location` may be `null` and `contingency` may be
   * `true` when the punch is registered without a confirmed geolocation, so the
   * backend can keep an auditable record of the fallback.
   *
   * `signal` (PLATFORM-3336) scopes the submit-only timeout set by
   * `useSubmitPunchcard` — never a global timeout on `apiClient`, which would also
   * affect export/PDF requests.
   */
  submitPunchcard: (payload: SubmitPunchcardPayload, options?: { signal?: AbortSignal }): Promise<void> =>
    apiClient
      .post(
        `/agreements/${payload.agreementUuid}/collects/punchcard`,
        {
          datetime: payload.datetime,
          type: PUNCHCARD_TYPE,
          context: {
            location: payload.location,
            contingency: payload.contingency,
            contingencyReason: payload.contingencyReason,
            accuracy: payload.accuracy,
            locationElapsedMs: payload.locationElapsedMs,
            locationSource: payload.locationSource,
            check: payload.check,
            timezone: payload.timezone,
          },
          collector: {
            uuid: COLLECTOR_UUID,
            type: COLLECTOR_TYPE,
            platform: COLLECTOR_PLATFORM,
          },
          photo: payload.photo,
          hash: '',
        },
        { signal: options?.signal },
      )
      .then(() => undefined),

  getAgreementInfo: async (agreementUuid: string): Promise<AgreementInfo> => {
    const detail = await apiClient
      .get<AgreementDetail>(`/agreements/${agreementUuid}`)
      .then((res) => res.data);

    const [contractorResult, workerResult] = await Promise.allSettled([
      detail.contractor
        ? apiClient
            .get(`/agreements/${agreementUuid}/contractor/${detail.contractor}`)
            .then((res) => res.data as AgreementInfo['contractor'])
        : Promise.resolve(null),
      detail.worker
        ? apiClient
            .get(`/agreements/${agreementUuid}/worker/${detail.worker}`)
            .then((res) => res.data as AgreementInfo['worker'])
        : Promise.resolve(null),
    ]);

    return {
      contractor: contractorResult.status === 'fulfilled' ? contractorResult.value : null,
      worker: workerResult.status === 'fulfilled' ? workerResult.value : null,
    };
  },
};
